import { HORIZON_COLOR_STYLE } from './horizon/config'
import { getHorizonPalette } from './horizon/palette'
import { assertStructuralThemeFolder } from './structuralThemeFolder'

export type ThemePalette = {
  colors: Record<string, string>
  typography: Record<string, string>
}

export type ThemeRegistryEntry = ThemePalette & {
  name: string
  description: string
  status: 'done' | 'todo' | 'backlog'
  preview: string | null
}

const horizonPalette = getHorizonPalette(HORIZON_COLOR_STYLE)

export const THEME_REGISTRY = {
  default: {
    name: 'Default',
    description: 'Current CSE Boost theme.',
    status: 'done',
    preview: null,
    colors: {
      primary: '#0B7A72',
      primary_hover: '#08635D',
      primary_active: '#064F4A',
      primary_disabled: '#B7DED9',
      primary_strong: '#08635D',
      primary_soft: '#E8F5F3',
      on_primary: '#FFFFFF',
      secondary: '#C2472F',
      secondary_hover: '#A33B28',
      secondary_soft: '#F8E7E2',
      accent: '#D99A2B',
      accent_soft: '#F8EDD5',
      background: '#F7FAFA',
      surface: '#FFFFFF',
      surface_strong: '#EEF5F4',
      surface_tint: '#F9FCFB',
      border: '#7C9590',
      border_strong: '#5E706C',
      border_soft: '#97AEA9',
      text_primary: '#10201D',
      text_secondary: '#5E706C',
      text_muted: '#6F817D',
      muted: '#EEF5F4',
      ring: '#0B7A72',
      success: '#0B7A72',
      success_soft: '#E8F5F3',
      warning: '#B45309',
      warning_soft: '#FEF3C7',
      error: '#A03434',
      error_soft: '#FEE2E2',
      info: '#1D4ED8',
      info_soft: '#DBEAFE',
      scrim: 'rgb(15 20 25 / 50%)',
    },
    typography: {
      heading_font: 'nunito',
      body_font: 'nunito',
    },
  },
  horizon: {
    name: 'Horizon',
    description: 'First structural theme for CSE Boost instances.',
    status: 'done',
    preview: null,
    colors: { ...horizonPalette },
    typography: {
      heading_font: 'inter',
      body_font: 'inter',
    },
  },
} as const satisfies Record<string, ThemeRegistryEntry>

export type ThemeSlug = keyof typeof THEME_REGISTRY

export function isThemeSlug(slug: string): slug is ThemeSlug {
  return Object.prototype.hasOwnProperty.call(THEME_REGISTRY, slug)
}

export function getActiveThemeSlug(rawSlug = import.meta.env.VITE_ACTIVE_THEME): ThemeSlug {
  const slug = typeof rawSlug === 'string' && rawSlug.trim() !== '' ? rawSlug.trim() : 'default'
  if (!isThemeSlug(slug)) {
    throw new Error(`Unknown VITE_ACTIVE_THEME [${slug}].`)
  }

  if (isStructuralThemeSlug(slug)) {
    assertStructuralThemeFolder(slug)
  }

  return slug
}

/** Structural themes ship overrides under `themes/{slug}/` (everything except `default`). */
export function isStructuralThemeSlug(slug: ThemeSlug): boolean {
  return slug !== 'default'
}
